import { Spinner } from 'amazon-chime-sdk-component-library-react'
import { AISummaryStatus, AISummaryType, useGetAISummary } from 'hooks/useGetAISummary'
import useHasFeature, { AppFeatures } from 'hooks/useHasFeature'
import AIGradient from 'images/ai-gradient.svg'
import AIPurple from 'images/ai-purple.svg'
import AIWhite from 'images/ai-white.svg'
import { ContactState } from 'store/case/case.state'
import styles from './AISummary.module.scss'

interface Props {
    contact: ContactState
    compact?: boolean
    acw?: boolean
    title?: string
    type?: AISummaryType
}

const AISummary: React.FC<Props> = ({
    compact,
    title = 'AI Summary',
    acw,
    type,
    contact,
    ...props
}) => {
    const hasFeature = useHasFeature()
    const hasPermission =
        hasFeature(AppFeatures.REDACTED_VOICE_CONTACT_SUMMARIES) ||
        hasFeature(AppFeatures.REDACTED_CHAT_SUMMARIES) ||
        hasFeature(AppFeatures.PRE_CONTACT_AI_SUMMARIES_EMAIL)

    const { status, summaryText } = useGetAISummary({
        contact,
        type: type ?? (acw ? AISummaryType.REALTIME_CHAT : AISummaryType.VOICE_CHAT),
    })

    const loading = status && status === AISummaryStatus.PENDING
    const error = status && status === AISummaryStatus.ERROR

    if (!hasPermission) {
        return <></>
    }
    if (compact) {
        return (
            <>
                {loading ? (
                    <div className={styles['sa-ai-summary-compact']}>
                        <div className={styles['gradient-logo-background']}>
                            <img src={AIWhite} title="AI" alt="AI" />
                        </div>
                        <div className={styles['sa-ai-summary-row']}>
                            <Spinner className={styles['sa-ai-summary-spinner']} />
                            <p>{summaryText}</p>
                        </div>
                    </div>
                ) : (
                    <div className={styles['sa-ai-summary-compact']}>
                        <div className={styles['gradient-logo-background']}>
                            <img src={AIWhite} title="AI" alt="AI" />
                        </div>
                        <span className={styles['compact-message']}>{summaryText}</span>
                    </div>
                )}
            </>
        )
    }

    return (
        <div className={styles['sa-ai-summary']} {...props} data-testid="sa-ai-summary">
            <div className={styles['inner-container']}>
                {loading ? (
                    <div className={styles['sa-ai-summary-no-data']}>
                        <img src={AIGradient} title="AI" alt="AI" />
                        <div className={styles['sa-ai-summary-row']}>
                            <Spinner className={styles['sa-ai-summary-spinner']} />
                            <p>{summaryText}</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className={styles['sa-ai-summary-no-data']}>
                        <img src={AIGradient} title="AI" alt="AI" />
                        <p className={styles['message']}>{summaryText}</p>
                    </div>
                ) : (
                    <>
                        <div className={styles['header']}>
                            <img src={AIGradient} title="AI" alt="AI" />
                            <span className={styles['heading']}>{title}</span>
                            <img
                                src={AIPurple}
                                title="AI"
                                alt="AI"
                                className={styles['icon-margin']}
                            />
                            <span className={styles['summary-source']}>Generated by AI</span>
                        </div>
                        <span className={styles['message']}>{summaryText}</span>
                    </>
                )}
            </div>
        </div>
    )
}

export default AISummary
